import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileImport, faFileExport, faDownload, faUpload, faSpinner, faCog, faChevronDown, faCheckCircle, faExclamationCircle, faLayerGroup, faCheck, faRuler, faShield, faChevronUp, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import StructureNode from '../components/StructureNode';
import StructureElementForm from '../components/StructureElementForm';
import { refreshFirebaseTokenIfNeeded, forceRefreshFirebaseToken, setActiveImportJobId } from '../utils/tokenRefresh';
import './BridgeDetail.css';

const BridgeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [bridge, setBridge] = useState(null);
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [levelConfig, setLevelConfig] = useState(null);
  const [loadedChildren, setLoadedChildren] = useState(new Set()); // Track which nodes have loaded children
  const [loadingChildren, setLoadingChildren] = useState(new Set()); // Track which nodes are currently loading children
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    direction: '',
    position: ''
  });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'frame', 'span', 'panelBay'
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingItem, setEditingItem] = useState(null);
  const [parentItem, setParentItem] = useState(null);
  
  // Import/Export state
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Area template / area data upload
  const [areaTemplateDownloading, setAreaTemplateDownloading] = useState(false);
  const [areaDataUploading, setAreaDataUploading] = useState(false);
  const areaDataFileInputRef = useRef(null);

  // Settings dropdown state
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  
  // Bridge structure info card state
  const [showStructureInfo, setShowStructureInfo] = useState(true);
  
  // Combination options
  const [combinations, setCombinations] = useState([]);
  
  // Directions for dropdown
  const [directions, setDirections] = useState([]);

  // Calculate CSV column count based on bridge configuration
  const calculateColumnCount = () => {
    if (!bridge || !bridge.levelLabels) return null;
    
    try {
      const levelLabels = typeof bridge.levelLabels === 'string' 
        ? JSON.parse(bridge.levelLabels) 
        : bridge.levelLabels;
      
      const enabledLevels = typeof bridge.enabledLevels === 'string'
        ? JSON.parse(bridge.enabledLevels)
        : bridge.enabledLevels || [];
      
      let columnCount = 0;
      
      enabledLevels.forEach(level => {
        const levelKey = `level${level}`;
        const levelConfig = levelLabels[levelKey] || {};
        
        // Name column (always present)
        columnCount += 1;
        
        // Direction and Position for Level 1
        if (level === 1) {
          columnCount += 2;
        }
        
        // Total Area if this level holds area data
        if (levelConfig.holdsAreaData) {
          columnCount += 1;
        }
        
        // Combination if this level holds exposure data
        if (levelConfig.holdsExposureClassData) {
          columnCount += 1;
        }
        
        // PID and Component Group ID removed - legacy fields from old bridge structure
      });
      
      return columnCount;
    } catch (error) {
      console.error('Error calculating column count:', error);
      return null;
    }
  };

  // Fetch data
  useEffect(() => {
    if (id) {
      fetchBridgeData();
      fetchCombinations();
      fetchDirections();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsDropdown && !event.target.closest('.settings-dropdown')) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsDropdown]);

  const fetchBridgeData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch bridge details
      const bridgeRes = await api.get(`/bridges/${id}`);
      const bridgeData = bridgeRes.data.data || bridgeRes.data;
      
      // Parse conditionRatingScale if it's a string
      if (bridgeData.conditionRatingScale && typeof bridgeData.conditionRatingScale === 'string') {
        try {
          bridgeData.conditionRatingScale = JSON.parse(bridgeData.conditionRatingScale);
        } catch (e) {
          console.error('Error parsing conditionRatingScale:', e);
        }
      }
      if (bridgeData.condition_rating_scale && typeof bridgeData.condition_rating_scale === 'string') {
        try {
          bridgeData.conditionRatingScale = JSON.parse(bridgeData.condition_rating_scale);
        } catch (e) {
          console.error('Error parsing condition_rating_scale:', e);
        }
      }
      
      setBridge(bridgeData);
      
      // Fetch bridge structure using new unified API (lazy loading - only top level)
      const structureRes = await api.get(`/bridge-structure/bridge/${id}`);
      const structureData = structureRes.data.data || structureRes.data;
      const bridgeInfo = structureRes.data.bridge || bridgeData;
      
      // Merge bridge info from structure API with bridge data (prioritize bridgeData for conditionRatingScale)
      if (bridgeInfo) {
        bridgeData.levelLabels = bridgeInfo.levelLabels || bridgeData.levelLabels;
        bridgeData.enabledLevels = bridgeInfo.enabledLevels || bridgeData.enabledLevels;
        // conditionRatingScale should already be parsed from bridgeData above
      }
      
      // Parse levelLabels if it's a string (needed for StructureNode to check holdsExposureClassData)
      let levelLabels = bridgeData.levelLabels || bridgeInfo?.levelLabels;
      if (typeof levelLabels === 'string') {
        try {
          levelLabels = JSON.parse(levelLabels);
        } catch (e) {
          console.error('Error parsing levelLabels:', e);
          levelLabels = null;
        }
      }
      // Update bridgeData with parsed levelLabels so StructureNode can use it
      bridgeData.levelLabels = levelLabels;
      
      // Also ensure conditionRatingScale is properly set on bridgeData
      // (it should already be parsed above, but ensure it's there)
      if (!bridgeData.conditionRatingScale && bridgeInfo?.conditionRatingScale) {
        let scale = bridgeInfo.conditionRatingScale;
        if (typeof scale === 'string') {
          try {
            scale = JSON.parse(scale);
          } catch (e) {
            console.error('Error parsing conditionRatingScale from bridgeInfo:', e);
          }
        }
        bridgeData.conditionRatingScale = scale;
      }
      if (typeof levelLabels === 'string') {
        try {
          levelLabels = JSON.parse(levelLabels);
        } catch (e) {
          levelLabels = null;
        }
      }
      
      // Parse enabledLevels if it's a string
      let enabledLevels = bridgeInfo.enabledLevels;
      if (typeof enabledLevels === 'string') {
        try {
          enabledLevels = JSON.parse(enabledLevels);
        } catch (e) {
          enabledLevels = null;
        }
      }
      // Convert to Set for faster lookup
      const enabledLevelsSet = enabledLevels && Array.isArray(enabledLevels) 
        ? new Set(enabledLevels) 
        : null;
      
      // Build level configuration
      const defaultLabels = {
        1: 'Frame',
        2: 'Span',
        3: 'Panel Bay',
        4: 'Component',
        5: 'Sub-Component',
        6: 'Girder',
        7: 'Component',
        8: 'Bolt'
      };
      
      const fullConfig = {};
      for (let i = 1; i <= 8; i++) {
        const levelKey = `level${i}`;
        if (levelLabels && levelLabels[levelKey]) {
          // Level is explicitly configured in levelLabels
          fullConfig[levelKey] = {
            label: levelLabels[levelKey].label || defaultLabels[i],
            enabled: levelLabels[levelKey].enabled !== false,
            holdsAreaData: levelLabels[levelKey].holdsAreaData || false,
            holdsExposureClassData: levelLabels[levelKey].holdsExposureClassData || false
          };
        } else {
          // Level is not in levelLabels - check enabledLevels array
          // Only enable if explicitly listed in enabledLevels array
          const isEnabled = enabledLevelsSet ? enabledLevelsSet.has(i) : false;
          fullConfig[levelKey] = {
            label: defaultLabels[i],
            enabled: isEnabled,
            holdsAreaData: false,
            holdsExposureClassData: false
          };
        }
      }
      
      setLevelConfig(fullConfig);
      
      // Set structure data (tree format from API - only root elements with empty children)
      setFrames(structureData); // Root elements (Level 1 or Level 2 if Level 1 disabled)
      
      // Reset loaded children state when refreshing
      setLoadedChildren(new Set());
      setExpandedNodes(new Set());
      
      setError(null);
    } catch (err) {
      console.error('Error fetching bridge data:', err);
      setError(err.response?.data?.error || 'Failed to load bridge data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Helper functions to get level labels
  const getLevelLabel = (level) => {
    if (!levelConfig) return level === 1 ? 'Frame' : level === 2 ? 'Span' : 'Panel Bay';
    return levelConfig[`level${level}`]?.label || (level === 1 ? 'Frame' : level === 2 ? 'Span' : 'Panel Bay');
  };

  const isLevelEnabled = (level) => {
    if (!levelConfig) return true;
    return levelConfig[`level${level}`]?.enabled !== false;
  };
  
  // Get enabled levels in order
  const getEnabledLevels = () => {
    if (!levelConfig) return [];
    const enabled = [];
    for (let i = 1; i <= 8; i++) {
      const levelKey = `level${i}`;
      if (levelConfig[levelKey]?.enabled) {
        enabled.push({
          level: i,
          config: levelConfig[levelKey]
        });
      }
    }
    return enabled;
  };
  
  // Check if any level holds area data (regardless of enabled status)
  const hasAreaDataLevel = () => {
    if (!levelConfig) return false;
    for (let i = 1; i <= 8; i++) {
      const levelKey = `level${i}`;
      if (levelConfig[levelKey]?.holdsAreaData) {
        return true;
      }
    }
    return false;
  };

  const fetchCombinations = async () => {
    try {
      const res = await api.get('/corrosion-damage-combinations');
      setCombinations(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching combinations:', err);
      setCombinations([]); // Set empty array as fallback
    }
  };

  const fetchDirections = async () => {
    try {
      const res = await api.get('/directions?isActive=true');
      setDirections(res.data?.data || res.data || []);
    } catch (err) {
      console.error('Error fetching directions:', err);
      setDirections([]); // Set empty array as fallback
    }
  };

  // Load children for a node (lazy loading)
  const loadChildren = async (elementId, element) => {
    // Check if already loaded or currently loading
    if (loadedChildren.has(elementId) || loadingChildren.has(elementId)) {
      return;
    }

    try {
      setLoadingChildren(prev => new Set(prev).add(elementId));
      
      const response = await api.get(`/bridge-structure/parent/${elementId}/children`);
      if (response.data.success) {
        const children = response.data.data || [];
        
        // Update the element in frames state with loaded children
        const updateElementWithChildren = (elements) => {
          return elements.map(el => {
            if (el.id === elementId) {
              return {
                ...el,
                children: children,
                hasChildren: children.length > 0 || el.hasChildren
              };
            }
            if (el.children && el.children.length > 0) {
              return {
                ...el,
                children: updateElementWithChildren(el.children)
              };
            }
            return el;
          });
        };
        
        setFrames(prev => updateElementWithChildren(prev));
        setLoadedChildren(prev => new Set(prev).add(elementId));
      }
    } catch (err) {
      console.error('Error loading children:', err);
      setError(err.response?.data?.error || 'Failed to load children');
    } finally {
      setLoadingChildren(prev => {
        const newSet = new Set(prev);
        newSet.delete(elementId);
        return newSet;
      });
    }
  };

  // Toggle node expansion (with lazy loading)
  const toggleNode = async (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      const isExpanding = !newSet.has(nodeId);
      
      if (isExpanding) {
        newSet.add(nodeId);
        
        // Extract element ID from nodeId (format: "structure-{id}")
        const elementId = parseInt(nodeId.replace('structure-', ''));
        
        // Find the element in the tree
        const findElement = (elements) => {
          for (const el of elements) {
            if (el.id === elementId) {
              return el;
            }
            if (el.children && el.children.length > 0) {
              const found = findElement(el.children);
              if (found) return found;
            }
          }
          return null;
        };
        
        const element = findElement(frames);
        
        // Load children if not already loaded and element has potential children
        if (element && !loadedChildren.has(elementId) && (element.hasChildren || !element.children || element.children.length === 0)) {
          loadChildren(elementId, element);
        }
      } else {
        newSet.delete(nodeId);
      }
      
      return newSet;
    });
  };

  // Auto-expand nodes with matching children when search is active
  useEffect(() => {
    if (!filters.search || frames.length === 0) return;

    const searchLower = filters.search.toLowerCase();
    const nodesToExpand = new Set();

    // Recursive function to find all nodes that should be expanded
    const findNodesToExpand = (element) => {
      let hasMatch = false;
      
      // Check if this element matches
      const elementName = (element.element_name || element.elementName || '').toString().toLowerCase();
      const elementCode = (element.element_code || element.elementCode || '').toString().toLowerCase();
      if (elementName.includes(searchLower) || elementCode.includes(searchLower)) {
        hasMatch = true;
      }

      // Check children
      if (element.children && Array.isArray(element.children) && element.children.length > 0) {
        element.children.forEach(child => {
          if (findNodesToExpand(child)) {
            hasMatch = true;
            // Expand this node if any child matches - use same format as StructureNode
            nodesToExpand.add(`structure-${element.id}`);
          }
        });
      }

      return hasMatch;
    };

    // Check all frames
    frames.forEach(frame => {
      findNodesToExpand(frame);
    });

    // Update expanded nodes
    if (nodesToExpand.size > 0) {
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        nodesToExpand.forEach(nodeId => newSet.add(nodeId));
        return newSet;
      });
    }
  }, [filters.search, frames]);

  // Recursive search function for tree structure
  const searchInElement = (element, searchLower) => {
    if (!element) return false;
    
    // Check current element - handle both snake_case and camelCase
    const elementName = (element.element_name || element.elementName || '').toString().toLowerCase();
    const elementCode = (element.element_code || element.elementCode || '').toString().toLowerCase();
    
    if (elementName.includes(searchLower) || elementCode.includes(searchLower)) {
      return true;
    }
    
    // Recursively check children
    if (element.children && Array.isArray(element.children) && element.children.length > 0) {
      return element.children.some(child => searchInElement(child, searchLower));
    }
    
    return false;
  };

  // Filter logic for unified structure
  const filterFrames = (frames) => {
    return frames.filter(element => {
      // Direction filter (only for Level 1 elements)
      if (filters.direction && element.direction && element.direction !== filters.direction) {
        return false;
      }
      
      // Position filter (only for Level 1 elements)
      if (filters.position && element.position && element.position !== filters.position) {
        return false;
      }
      
      // Search filter (recursive through tree)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (!searchInElement(element, searchLower)) {
          return false;
        }
      }
      
      return true;
    });
  };


  // CRUD handlers - Updated for unified structure
  const handleAdd = (levelNumber, parent = null) => {
    setModalType(levelNumber); // Store level number instead of type
    setModalMode('create');
    setParentItem(parent);
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (element) => {
    const levelNumber = element.levelNumber || element.level_number;
    setModalType(levelNumber);
    setModalMode('edit');
    setEditingItem(element);
    setParentItem(null);
    setShowModal(true);
  };

  const handleDelete = async (element) => {
    try {
      const levelNumber = element.levelNumber || element.level_number;
      const levelLabel = getLevelLabel(levelNumber);
      
      if (!window.confirm(`Are you sure you want to delete this ${levelLabel}?`)) {
        return;
      }
      
      await api.delete(`/bridge-structure/${element.id}`);
      await fetchBridgeData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to delete element';
      if (errorMsg.includes('children')) {
        alert(errorMsg);
      } else {
        alert(`Failed to delete: ${errorMsg}`);
      }
    }
  };

  const handleUpdateCondition = async () => {
    try {
      if (!window.confirm('This will calculate and update the current condition (degradation percentage) for all elements at the exposure class level based on their last repair year. Continue?')) {
        return;
      }

      setLoading(true);
      const response = await api.post(`/bridges/${id}/update-condition`);
      
      if (response.data.success) {
        alert(`Successfully updated condition for ${response.data.data.updated} of ${response.data.data.total} elements.`);
        await fetchBridgeData(); // Refresh to show updated condition
      } else {
        alert(response.data.message || 'Failed to update condition');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to update condition';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData, elementId = null) => {
    try {
      const levelNumber = typeof modalType === 'number' ? modalType : 
                         (editingItem?.levelNumber || editingItem?.level_number);
      
      if (!levelNumber) {
        alert('Invalid level number');
        return;
      }

      const isEditMode = !!elementId || modalMode === 'edit';
      const endpoint = isEditMode 
        ? `/bridge-structure/${elementId || editingItem.id}`
        : '/bridge-structure';

      const method = isEditMode ? 'put' : 'post';

      // Ensure bridgeId is set
      formData.bridgeId = parseInt(id);
      formData.levelNumber = levelNumber;

      // Set parentId: 
      // 1. If parentItem exists (when adding new child), use it
      // 2. If editing and parentItem is null, preserve existing parentId from the element
      if (parentItem && parentItem.id) {
        formData.parentId = parentItem.id;
      } else if (isEditMode && editingItem) {
        // Preserve existing parentId when editing
        const existingParentId = editingItem.parentId || editingItem.parent_id;
        if (existingParentId !== undefined && existingParentId !== null) {
          formData.parentId = existingParentId;
        }
      }

      await api[method](endpoint, formData);
      setShowModal(false);
      await fetchBridgeData();
    } catch (err) {
      let errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to save element';
      
      // If there are detailed error messages, show them
      if (err.response?.data?.details && Array.isArray(err.response.data.details)) {
        errorMsg = err.response.data.details.join('\n');
      }
      
      alert(errorMsg);
    }
  };

  // Import/Export handlers
  const handleDownloadTemplate = async () => {
    try {
      // Pass bridgeId to get bridge-specific template
      const response = await api.get(`/bridge-import/template?bridgeId=${id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bridge-${id}-import-template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template');
    }
  };

  const handleExportBridge = async () => {
    try {
      setExporting(true);
      const response = await api.get(`/bridge-import/export/${id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bridge-${id}-export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting bridge:', error);
      alert('Failed to export bridge data');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadAreaTemplate = async () => {
    try {
      setAreaTemplateDownloading(true);
      setShowSettingsDropdown(false);
      const response = await api.get(`/bridges/${id}/area-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bridge-${id}-area-template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading area template:', error);
      let msg = 'Failed to download area template';
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const j = JSON.parse(text);
            msg = j.message || j.error || msg;
          } catch (_) {}
        } else {
          msg = error.response.data.message || error.response.data.error || msg;
        }
      }
      alert(msg);
    } finally {
      setAreaTemplateDownloading(false);
    }
  };

  const handleUploadAreaData = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      event.target.value = '';
      return;
    }
    try {
      setAreaDataUploading(true);
      setShowSettingsDropdown(false);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/bridges/${id}/area-data`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { stats } = response.data || {};
      const message = stats
        ? `Area data uploaded. Processed: ${stats.processed ?? 0}, Updated: ${stats.updated ?? 0}, Skipped: ${stats.skipped ?? 0}.`
        : (response.data?.message || 'Area data uploaded successfully.');
      alert(message);
      fetchBridgeData();
    } catch (error) {
      console.error('Error uploading area data:', error);
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to upload area data';
      alert(typeof msg === 'string' ? msg : 'Failed to upload area data');
    } finally {
      setAreaDataUploading(false);
      event.target.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const [importJobId, setImportJobId] = useState(null);
  const [importStatus, setImportStatus] = useState(null);

  // Poll for import status if job is in progress
  useEffect(() => {
    if (!importJobId) {
      // Clear active import when job ID is cleared
      setActiveImportJobId(null);
      return;
    }

    // Set active import job ID for graceful error handling
    setActiveImportJobId(importJobId);

    const pollInterval = setInterval(async () => {
      try {
        // Refresh token before polling to prevent expiration during long imports
        await refreshFirebaseTokenIfNeeded();
        
        const response = await api.get(`/bridge-import/status/${importJobId}`);
        const status = response.data;
        setImportStatus(status);

        // Stop polling if job is completed or failed
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(pollInterval);
          setImportJobId(null);
          setImportStatus(null); // Clear progress indicator
          setImporting(false); // Reset importing state so button shows normal state
          setSelectedFile(null); // Clear selected file
          document.getElementById('import-file-input').value = ''; // Reset file input
          setActiveImportJobId(null); // Clear active import flag
          
          if (status.status === 'completed') {
            setImportResult({
              success: true,
              message: status.result?.message || 'Import completed successfully',
              stats: status.result?.stats
            });
            // Refresh bridge data
            fetchBridgeData();
          } else {
            setImportResult({
              success: false,
              message: status.error || 'Import failed',
              stats: status.result?.stats
            });
          }
        }
      } catch (error) {
        console.error('Error polling import status:', error);
        
        // If 401 error, try to refresh token and retry
        if (error.response?.status === 401) {
          try {
            console.log('Token expired during import polling, refreshing...');
            const refreshedToken = await forceRefreshFirebaseToken();
            if (refreshedToken) {
              // Retry the request with refreshed token
              try {
                const response = await api.get(`/bridge-import/status/${importJobId}`);
                const status = response.data;
                setImportStatus(status);
                
                // Check if job completed
                if (status.status === 'completed' || status.status === 'failed') {
                  clearInterval(pollInterval);
                  setImportJobId(null);
                  setImportStatus(null);
                  setImporting(false);
                  setSelectedFile(null);
                  document.getElementById('import-file-input').value = '';
                  setActiveImportJobId(null);
                  
                  if (status.status === 'completed') {
                    setImportResult({
                      success: true,
                      message: status.result?.message || 'Import completed successfully',
                      stats: status.result?.stats
                    });
                    fetchBridgeData();
                  } else {
                    setImportResult({
                      success: false,
                      message: status.error || 'Import failed',
                      stats: status.result?.stats
                    });
                  }
                }
              } catch (retryError) {
                console.error('Error retrying after token refresh:', retryError);
                // Continue polling - might be a temporary issue
              }
            } else {
              console.error('Failed to refresh token during import polling');
              // Continue polling - don't logout during active import
            }
          } catch (refreshError) {
            console.error('Error refreshing token during import:', refreshError);
            // Continue polling - don't logout during active import
          }
        }
        // For other errors, continue polling (might be temporary)
      }
    }, 60000); // Poll every 60 seconds (1 minute)

    return () => {
      clearInterval(pollInterval);
      // Clear active import when component unmounts or job ID changes
      if (!importJobId) {
        setActiveImportJobId(null);
      }
    };
  }, [importJobId, fetchBridgeData]);

  const handleImport = async () => {
    if (!selectedFile) {
      alert('Please select a CSV file to import');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', selectedFile);
    formDataUpload.append('bridgeId', id);

    try {
      setImporting(true);
      setImportResult(null);
      setImportStatus(null);
      setImportJobId(null);

      const response = await api.post('/bridge-import/import', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 2 minutes timeout (allows for file upload + database job creation)
      });

      if (response.data.jobId) {
        // Async import started - store job ID and start polling
        setImportJobId(response.data.jobId);
        setImportStatus({
          status: 'processing',
          progress: {
            totalRows: 0,
            processedRows: 0,
            percentage: 0,
            currentBatch: 0,
            totalBatches: 0
          }
        });
        // Don't set importResult here - only show progress indicator
        // importResult will be set by polling when job completes
        // Don't clear file or reset importing yet - will be done when job completes
      } else {
        // Legacy synchronous response (fallback)
        setImportResult({
          success: response.data.success,
          message: response.data.message,
          stats: response.data.stats
        });

        if (response.data.success) {
          fetchBridgeData();
        }
        setImporting(false);
        setSelectedFile(null);
        document.getElementById('import-file-input').value = '';
      }
    } catch (error) {
      console.error('Error starting import:', error);
      setImportResult({
        success: false,
        message: error.response?.data?.message || 'Failed to start import',
        stats: error.response?.data?.stats || null
      });
      setImporting(false);
      setSelectedFile(null);
    }
  };

  // Get unique values for filters
  const uniqueDirections = [...new Set(frames.map(f => f.direction).filter(Boolean))];
  const uniquePositions = [...new Set(frames.map(f => f.position).filter(Boolean))];

  if (loading) {
    return (
      <div className="bridge-detail-page">
        <div className="loading">Loading bridge data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bridge-detail-page">
        <div className="error">{error}</div>
        <button onClick={() => navigate('/bridges')}>Back to Bridges</button>
      </div>
    );
  }

  const filteredFrames = filterFrames(frames);
  
  // Debug: Log search results
  if (filters.search) {
    console.log('Search term:', filters.search);
    console.log('Total frames:', frames.length);
    console.log('Filtered frames:', filteredFrames.length);
    if (filteredFrames.length > 0) {
      console.log('First filtered frame:', filteredFrames[0]);
    }
  }

  return (
    <div className="bridge-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button onClick={() => navigate('/bridges')} className="back-button">
            ← Back
          </button>
        </div>
        <div className="header-center">
          <h1 className="bridge-name">{bridge?.bridgeName || 'Loading...'}</h1>
          <p className="bridge-subtitle">Bridge ID: {id}</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleUpdateCondition} 
            className="btn-secondary"
            title="Calculate and update current condition for all elements at exposure class level"
            style={{ marginRight: '10px' }}
          >
            <FontAwesomeIcon icon={faCog} className="mr-2" />
            Update Condition
          </button>
          <div className="settings-dropdown">
            <button 
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              className="btn-settings"
              disabled={exporting || importing || areaTemplateDownloading || areaDataUploading}
            >
              <FontAwesomeIcon icon={faCog} className="mr-2" />
              Data Management
              <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
            </button>
            {showSettingsDropdown && (
              <div className="dropdown-menu">
                <button onClick={handleDownloadTemplate} className="dropdown-item">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Download Template
                </button>
                <button onClick={() => {
                  setShowImportModal(true);
                  setShowSettingsDropdown(false);
                }} className="dropdown-item">
                  <FontAwesomeIcon icon={faFileImport} className="mr-2" />
                  Import CSV
                </button>
                <button 
                  onClick={() => {
                    handleExportBridge();
                    setShowSettingsDropdown(false);
                  }} 
                  disabled={exporting} 
                  className="dropdown-item"
                >
                  <FontAwesomeIcon icon={exporting ? faSpinner : faFileExport} className="mr-2" spin={exporting} />
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </button>
                <button
                  onClick={handleDownloadAreaTemplate}
                  disabled={areaTemplateDownloading}
                  className="dropdown-item"
                >
                  <FontAwesomeIcon icon={areaTemplateDownloading ? faSpinner : faDownload} className="mr-2" spin={areaTemplateDownloading} />
                  {areaTemplateDownloading ? 'Downloading...' : 'Download Area Template'}
                </button>
                <input
                  ref={areaDataFileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleUploadAreaData}
                  id="area-data-file-input"
                />
                <button
                  onClick={() => {
                    areaDataFileInputRef.current?.click();
                  }}
                  disabled={areaDataUploading}
                  className="dropdown-item"
                >
                  <FontAwesomeIcon icon={areaDataUploading ? faSpinner : faUpload} className="mr-2" spin={areaDataUploading} />
                  {areaDataUploading ? 'Uploading...' : 'Upload Area Data'}
                </button>
              </div>
            )}
          </div>
          {isLevelEnabled(1) ? (
            <button onClick={() => handleAdd(1)} className="btn-primary">
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add {getLevelLabel(1)}
            </button>
          ) : isLevelEnabled(2) && (
            <button onClick={() => handleAdd(2)} className="btn-primary">
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add {getLevelLabel(2)}
            </button>
          )}
        </div>
      </div>

      {/* Bridge Structure Info Card */}
      {levelConfig && (
        <div className="bridge-structure-info-card">
          <div 
            className="structure-info-header"
            onClick={() => setShowStructureInfo(!showStructureInfo)}
          >
            <div className="structure-info-title">
              <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
              <span>Bridge Structure Configuration</span>
            </div>
            <FontAwesomeIcon 
              icon={showStructureInfo ? faChevronUp : faChevronDown} 
              className="structure-info-toggle"
            />
          </div>
          
          {showStructureInfo && (
            <div className="structure-info-content">
              {/* Warning if no area data level is defined */}
              {!hasAreaDataLevel() && (
                <div className="structure-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                  <div className="warning-content">
                    <strong>Warning:</strong> No level is configured to hold area data. 
                    Area data is required for accurate cost calculations in simulations. 
                    Please configure at least one level with "Holds Area Data" enabled in the bridge settings.
                  </div>
                </div>
              )}
              
              {getEnabledLevels().length > 0 ? (
                <div className="structure-levels-tree">
                  {getEnabledLevels().map((levelData, index) => {
                    const { level, config } = levelData;
                    const isLast = index === getEnabledLevels().length - 1;
                    
                    return (
                      <div key={level} className="structure-level-item">
                        <div className="level-connector">
                          {index > 0 && <div className="connector-line"></div>}
                          {!isLast && <div className="connector-line-vertical"></div>}
                        </div>
                        <div className="level-content">
                          <div className="level-header">
                            <span className="level-number">Level {level}</span>
                            <span className="level-label">: {config.label}</span>
                            <div className="level-badges">
                              <span className="badge badge-enabled">
                                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                Enabled
                              </span>
                              {level === 1 && (
                                <>
                                  <span className="badge badge-info">
                                    Direction
                                  </span>
                                  <span className="badge badge-info">
                                    Position
                                  </span>
                                </>
                              )}
                              {config.holdsAreaData && (
                                <span className="badge badge-area">
                                  <FontAwesomeIcon icon={faRuler} className="mr-1" />
                                  Area Data
                                </span>
                              )}
                              {config.holdsExposureClassData && (
                                <span className="badge badge-exposure">
                                  <FontAwesomeIcon icon={faShield} className="mr-1" />
                                  Exposure Class
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="structure-info-empty">
                  <p>No levels are currently enabled for this bridge.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder={isLevelEnabled(1) 
            ? `Search ${getLevelLabel(1).toLowerCase()}s, ${getLevelLabel(2).toLowerCase()}s, or ${getLevelLabel(3).toLowerCase()}s...`
            : `Search ${getLevelLabel(2).toLowerCase()}s or ${getLevelLabel(3).toLowerCase()}s...`}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="search-input"
        />
        {isLevelEnabled(1) && (
          <>
            <select
              value={filters.direction}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
              className="filter-select"
            >
              <option value="">All Directions</option>
              {uniqueDirections.map(dir => (
                <option key={dir} value={dir}>{dir}</option>
              ))}
            </select>
            <select
              value={filters.position}
              onChange={(e) => setFilters({ ...filters, position: e.target.value })}
              className="filter-select"
            >
              <option value="">All Positions</option>
              {uniquePositions.map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </>
        )}
        {(filters.search || filters.direction || filters.position) && (
          <button
            onClick={() => setFilters({ search: '', direction: '', position: '' })}
            className="btn-clear"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Tree View */}
      <div className="tree-view" style={{ overflowAnchor: 'none' }}>
        {filteredFrames.length === 0 ? (
          <div className="no-data">
            <p>No structure elements found for this bridge{filters.search || filters.direction || filters.position ? ' matching the filters' : ''}.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {isLevelEnabled(1) && (
                <button onClick={() => handleAdd(1)} className="btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Add {getLevelLabel(1)}
                </button>
              )}
              {!isLevelEnabled(1) && isLevelEnabled(2) && (
                <button onClick={() => handleAdd(2)} className="btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Add {getLevelLabel(2)}
                </button>
              )}
              <button onClick={() => setShowImportModal(true)} className="btn-secondary">
                <FontAwesomeIcon icon={faFileImport} className="mr-2" />
                Import CSV
              </button>
              <button 
                onClick={handleUpdateCondition} 
                className="btn-secondary"
                title="Calculate and update current condition for all elements at exposure class level"
              >
                <FontAwesomeIcon icon={faCog} className="mr-2" />
                Update Condition
              </button>
            </div>
          </div>
        ) : (
          filteredFrames.map(element => (
            <StructureNode
              key={element.id}
              element={element}
              bridge={bridge}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              onAdd={handleAdd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              getLevelLabel={getLevelLabel}
              isLevelEnabled={isLevelEnabled}
              loadingChildren={loadingChildren}
              loadedChildren={loadedChildren}
            />
          ))
        )}
      </div>

      {/* Structure Element Form Modal */}
      {showModal && typeof modalType === 'number' && levelConfig && (
        <StructureElementForm
          bridge={bridge}
          levelNumber={modalType}
          element={editingItem}
          parentElement={parentItem}
          combinations={combinations}
          directions={directions}
          levelConfig={levelConfig[`level${modalType}`]}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          selectedFile={selectedFile}
          onFileSelect={handleFileSelect}
          onImport={handleImport}
          onClose={() => {
            setShowImportModal(false);
            setImportResult(null);
            setImportStatus(null);
            setImportJobId(null);
            setSelectedFile(null);
          }}
          onDownloadTemplate={handleDownloadTemplate}
          importing={importing}
          importResult={importResult}
          importStatus={importStatus}
          columnCount={calculateColumnCount()}
          uniquePositions={uniquePositions}
          uniqueDirections={[...new Set(frames.map(f => f.direction).filter(Boolean))]}
        />
      )}

    </div>
  );
};

// Old node components removed - now using StructureNode component
// (Removed to avoid parsing errors with JSX in comment blocks)

// Import Modal Component
const ImportModal = ({ selectedFile, onFileSelect, onImport, onClose, onDownloadTemplate, importing, importResult, importStatus, columnCount, uniquePositions = [], uniqueDirections = [] }) => {
  // Generate instruction text with dynamic column count
  const getInstructionText = () => {
    if (columnCount !== null && columnCount !== undefined) {
      return `Upload a CSV file with the correct format (${columnCount} ${columnCount === 1 ? 'column' : 'columns'}).`;
    }
    return 'Upload a CSV file with the correct format.';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content import-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import Bridge Data</h2>
          <button onClick={onClose} className="close-btn" aria-label="Close">×</button>
        </div>
        
        <div className="modal-body">
          {/* Instructions Section */}
          <div className="import-instructions">
            <p>
              <strong>Instructions:</strong> {getInstructionText()}{' '}
              <button 
                type="button"
                onClick={onDownloadTemplate} 
                className="template-download-link"
                disabled={importing}
              >
                <FontAwesomeIcon icon={faDownload} className="mr-1" />
                Download the template
              </button>
              {' '}to see the required structure.
            </p>
            
            {/* Position and Direction Guidance */}
            {(uniquePositions.length > 0 || uniqueDirections.length > 0) && (
              <div className="import-guidance" style={{ marginTop: '15px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '4px', fontSize: '14px' }}>
                <strong style={{ display: 'block', marginBottom: '8px' }}>Column Value Guidance:</strong>
                {uniqueDirections.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Direction:</strong> Use values like: {uniqueDirections.slice(0, 5).join(', ')}
                    {uniqueDirections.length > 5 && ` (and ${uniqueDirections.length - 5} more)`}
                  </div>
                )}
                {uniquePositions.length > 0 && (
                  <div>
                    <strong>Position:</strong> Used with Direction to uniquely identify Level 1 elements. 
                    Common values in this bridge: <strong>{uniquePositions.join(', ')}</strong>
                    <br />
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Position typically indicates location type (e.g., "Internal", "External") or numeric position (e.g., "1", "2", "3").
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* File Upload Section */}
          <div className="file-upload-section">
            <label htmlFor="import-file-input" className="file-upload-label">
              Select CSV File:
            </label>
            <div className="file-input-wrapper">
              <input
                id="import-file-input"
                type="file"
                accept=".csv"
                onChange={onFileSelect}
                disabled={importing}
                className="file-input"
              />
              {selectedFile ? (
                <div className="file-selected">
                  <FontAwesomeIcon icon={faFileImport} className="file-icon" />
                  <div className="file-info">
                    <span className="file-name">{selectedFile.name}</span>
                    <span className="file-size">({(selectedFile.size / 1024).toFixed(2)} KB)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      document.getElementById('import-file-input').value = '';
                      onFileSelect({ target: { files: [] } });
                    }}
                    className="file-remove-btn"
                    disabled={importing}
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="file-placeholder">
                  <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                  <span>No file chosen</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Import Progress */}
          {importStatus && importStatus.status === 'processing' && (
            <div className="import-result processing">
              <div className="result-header">
                <FontAwesomeIcon icon={faSpinner} spin className="result-icon" />
                <h3>Import in Progress</h3>
              </div>
              {importStatus.progress && (
                <div className="import-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${importStatus.progress.percentage || 0}%` }}
                    ></div>
                  </div>
                  <p className="progress-text">
                    Processed: {importStatus.progress.processedRows || 0} / {importStatus.progress.totalRows || 0} rows 
                    ({importStatus.progress.percentage || 0}%)
                  </p>
                  {importStatus.progress.totalBatches > 0 && (
                    <p className="progress-text">
                      Batch: {importStatus.progress.currentBatch || 0} / {importStatus.progress.totalBatches}
                    </p>
                  )}
                  <p className="progress-note" style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Status updates every minute. Please keep this window open.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Import Results */}
          {importResult && (
            <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
              <div className="result-header">
                <FontAwesomeIcon 
                  icon={importResult.success ? faCheckCircle : faExclamationCircle} 
                  className={`result-icon ${importResult.success ? 'success-icon' : 'error-icon'}`}
                />
                <h3>{importResult.success ? 'Import Successful' : 'Import Failed'}</h3>
              </div>
              <p className="result-message">{importResult.message}</p>
              
              {importResult.stats && (
                <div className="result-stats">
                  {importResult.stats.elementsByLevel && (
                    <div className="stats-grid">
                      {Object.entries(importResult.stats.elementsByLevel).map(([level, count]) => (
                        <div key={level} className="stat-item">
                          <span className="stat-label">Level {level}:</span>
                          <span className="stat-value">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {importResult.stats.errors && importResult.stats.errors.length > 0 && (
                    <div className="errors-list">
                      <h4>Errors:</h4>
                      <ul>
                        {importResult.stats.errors.slice(0, 10).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {importResult.stats.errors.length > 10 && (
                          <li className="more-errors">... and {importResult.stats.errors.length - 10} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {importResult.stats.warnings && importResult.stats.warnings.length > 0 && (
                    <div className="warnings-list">
                      <h4>Warnings:</h4>
                      <ul>
                        {importResult.stats.warnings.slice(0, 5).map((warning, i) => (
                          <li key={i}>{warning}</li>
                        ))}
                        {importResult.stats.warnings.length > 5 && (
                          <li className="more-warnings">... and {importResult.stats.warnings.length - 5} more warnings</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Modal Actions */}
        <div className="modal-actions">
          <button 
            onClick={onClose} 
            className="btn-secondary" 
            disabled={importing}
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={onImport}
            className="btn-primary import-btn"
            disabled={!selectedFile || importing}
            type="button"
          >
            {importing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Importing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Import Data
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BridgeDetail;
